import * as React from "react";
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from "@modules/datasheets/types/datasheet-element";
import { generateId } from "@common/utils/common";
import { DatasheetTextComponentGetDto } from "@common/dto/datasheet/text-component/get-dto";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetFieldComponentOriginFieldGetDto } from "@common/dto/datasheet/field-component/origin-field-dto";

type Props = {
  selectedDatasheetElementsId: Array<DatasheetElementId>;
  datasheetElementsOnCurrentTab: Record<DatasheetElementId, DatasheetElement>;
  getDatasheetElement: (id: DatasheetElementId) => DatasheetElement | undefined;
  onDatasheetElementsCopy: (datasheetElements: Array<DatasheetElement>) => void;
  onDatasheetElementsCopyReset: () => void;
  onDatasheetElementsChange: (datasheetElements: Record<DatasheetElementId, DatasheetElement>) => void;
  doesFieldExistOnCurrentTab: (originalField: DatasheetFieldComponentOriginFieldGetDto) => boolean;
  copiedDatasheetElements: Array<DatasheetElement>;
  getUniqueDatasheetElementId: (originalField: DatasheetFieldComponentOriginFieldGetDto) => string;
};

export const useDatasheetElementsCopyPasteController = (props: Props) => {
  const { 
    selectedDatasheetElementsId,
    getDatasheetElement,
    datasheetElementsOnCurrentTab,
    onDatasheetElementsCopy,
    copiedDatasheetElements,
    doesFieldExistOnCurrentTab,
    getUniqueDatasheetElementId,
    onDatasheetElementsCopyReset,
    onDatasheetElementsChange,
  } = props;

  const handleDatasheetElementsCopy = React.useCallback(() => {
    const elements = selectedDatasheetElementsId.map(id => getDatasheetElement(id)).filter(Boolean) as Array<DatasheetElement>;

    onDatasheetElementsCopy(elements);
  }, [selectedDatasheetElementsId, onDatasheetElementsCopy, getDatasheetElement]);

  const handleDatasheetElementsPaste = () => {
    type ReduceResult = {
      hasDuplicatedDatasheetElements: boolean;
      textComponentsToCopy: Record<DatasheetElementId, DatasheetElement>;
      fieldComponentsToCopy: Record<DatasheetElementId, DatasheetElement>;
    };

    let tabOrder = 0;

    const processedElements = copiedDatasheetElements.reduce<ReduceResult>((acc, element) => {
      if (element.type === DatasheetElementType.text) {
        const newId = generateId(); 

        const newElement: DatasheetElement<DatasheetTextComponentGetDto> = {
          ...element,
          id: newId,
          original: {
            ...(element as DatasheetElement<DatasheetTextComponentGetDto>).original,
          }
        };

        acc.textComponentsToCopy[newId] = newElement;

        return acc;
      }

      const datasheetElementFieldComponent = element as DatasheetElement<DatasheetFieldComponentGetDto>;
      const fieldExists = doesFieldExistOnCurrentTab(datasheetElementFieldComponent.original.field);

      if (fieldExists) {
        acc.hasDuplicatedDatasheetElements = true;

        return acc;
      } 

      const newId = getUniqueDatasheetElementId(datasheetElementFieldComponent.original.field);

      tabOrder = tabOrder + 1;

      const newElement: DatasheetElement<DatasheetFieldComponentGetDto> = {
        ...datasheetElementFieldComponent,
        id: newId,
        original: {
          ...datasheetElementFieldComponent.original,
          taborder: tabOrder,
        }
      };

      acc.fieldComponentsToCopy[newId] = newElement;

      return acc;
    }, { hasDuplicatedDatasheetElements: false, textComponentsToCopy: {}, fieldComponentsToCopy: {} });

    const hasAnyElementsToCopy = (
      Object.keys(processedElements.fieldComponentsToCopy).length > 0 || 
      Object.keys(processedElements.textComponentsToCopy).length > 0
    );

    if (hasAnyElementsToCopy) {
      onDatasheetElementsChange({
        ...processedElements.fieldComponentsToCopy,
        ...processedElements.textComponentsToCopy,
        ...datasheetElementsOnCurrentTab,
      });
    }
    onDatasheetElementsCopyReset();

    return {
      hasDuplicatedDatasheetElements: processedElements.hasDuplicatedDatasheetElements
    };
  };

  const copiedDatasheetElementsCount = React.useMemo(() => copiedDatasheetElements.length, [copiedDatasheetElements]);

  return {
    handleDatasheetElementsCopy,
    copiedDatasheetElementsCount,
    handleDatasheetElementsPaste,
  };
};